import {formatHex, formatRgb, interpolate, parse} from 'culori'
import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'

// Ramka zaznaczania na tablicy: odcien akcentu, a nie mleczna mgla PrimaryContainer nad elementami
// (rysowane jeszcze z globalAlpha 0.35 -> ok. 9 % akcentu)
const WHITEBOARD_MARQUEE_FILL_ALPHA = 0.25

function withAlpha(color: string, alpha: number): string {
  const parsedColor = parse(color)
  if (!parsedColor) {
    return color
  }
  return formatRgb({
    ...parsedColor,
    alpha,
  })
}

// odpowiednik color-mix(in srgb, <color> <amount>, <base>) z light-modern-theme.scss
function mixInto(base: string, color: string, amount: number): string {
  return formatHex(interpolate([base, color], 'rgb')(amount)) ?? base
}

// Zmienne light-modern liczone z innych zmiennych motywu - po nadpisaniach palety i obrysu karty
// (useThemeVariables), wiec kazda paleta dostaje swoje wartosci bez wpisow w
// LightModernVariantOverridesMap. Fiord Night i Dark maja te zmienne jako literaly
export function getLightModernDerivedVariables(variables: IThemeVariables): Partial<IThemeVariables> {
  return {
    // wykresy: tekst na karcie jak tekst tabeli, siatka jak siatka tabeli, chip sumy jak licznik grupy, tooltip = biala karta
    [ThemeVariableType.ChartLabelOnSurfaceColor]: variables[ThemeVariableType.ContentNormalColor],
    [ThemeVariableType.ChartGridColor]: variables[ThemeVariableType.DatabaseTableGridColor],
    [ThemeVariableType.ChartTotalBadgeBackgroundColor]: variables[ThemeVariableType.SecondaryContainerColor],
    [ThemeVariableType.ChartTotalBadgeTextColor]: variables[ThemeVariableType.OnSecondaryContainerColor],
    [ThemeVariableType.ChartTooltipBackgroundColor]: variables[ThemeVariableType.SurfaceBrightColor],
    [ThemeVariableType.ChartTooltipTitleColor]: variables[ThemeVariableType.ContentBrightColor],
    [ThemeVariableType.ChartTooltipBodyColor]: variables[ThemeVariableType.ContentNormalColor],
    [ThemeVariableType.ChartTooltipBorderColor]: variables[ThemeVariableType.DatabaseTableCardBorderColor],
    [ThemeVariableType.ChartTooltipBorderWidth]: 1,
    // kanwa automatyzacji: modul bez koloru aplikacji i krok w trakcie o stopien i dwa ciemniejsze od kanwy
    // (ContainerLow w TS light to biel - krag znikal)
    [ThemeVariableType.AutomationCanvasDefaultModuleColor]: variables[ThemeVariableType.ContainerHighColor],
    [ThemeVariableType.AutomationCanvasRunInProgressColor]: variables[ThemeVariableType.ContainerHighestColor],
    // tablica: rog karteczki w cieniu atramentu palety (ContentDarkest w light jest jasny i rozjasnial rog),
    // marquee w odcieniu akcentu palety
    [ThemeVariableType.WhiteboardStickyNoteFoldShadeColor]: withAlpha(variables[ThemeVariableType.DatabaseTableStickyShadowColor], 1),
    [ThemeVariableType.WhiteboardMarqueeFillColor]: withAlpha(variables[ThemeVariableType.PrimaryColor], WHITEBOARD_MARQUEE_FILL_ALPHA),
    // pole Lokalizacja: znacznik i strefa zasiegu w akcencie palety zamiast blekitu spoza palety
    [ThemeVariableType.MapPickerMarkerColor]: variables[ThemeVariableType.PrimaryColor],
    [ThemeVariableType.MapPickerAreaColor]: variables[ThemeVariableType.PrimaryContainerColor],
    // etykieta slupka time trackera: biala cyfra z obrysem w kolorze slupka - czytelna i na slupku, i na bialym
    // panelu, gdy etykieta jest szersza od slupka
    [ThemeVariableType.TimeTrackerChartLabelStrokeColor]: variables[ThemeVariableType.TimeTrackerChartBarColor],
    // plakietki sortowania i filtra w naglowkach kolumn - kolor wlaczonego filtra jak w naglowku bazy
    // (--filter-active-* w light-modern-theme.scss): tlo primary-container-hover, hover o 22 % primary mocniej
    [ThemeVariableType.FilterActiveBackgroundColor]: variables[ThemeVariableType.PrimaryContainerHoverColor],
    [ThemeVariableType.FilterActiveBackgroundHoverColor]: mixInto(variables[ThemeVariableType.PrimaryContainerHoverColor], variables[ThemeVariableType.PrimaryColor], 0.22),
    [ThemeVariableType.FilterActiveContentColor]: variables[ThemeVariableType.OnPrimaryContainerColor],
    [ThemeVariableType.FilterActiveContentHoverColor]: variables[ThemeVariableType.OnPrimaryContainerColor],
  }
}
